import { useEffect, useState } from 'react';
import { Alert, TextField, Typography } from '@mui/material';
import { toast } from 'sonner';

import { useSendTestEmailMutation } from '@/apis/extended/admin/emailApi';
import { CommonDialog } from '@/components/Common/CommonDialog';
import { useAuth } from '@/hooks/useAuth';
import { Email } from '@/types/EmailType';

type SendTestEmailModalProps = {
  isOpen: boolean;
  email: Email | null;
  onClose: () => void;
};

export const SendTestEmailModal = ({
  isOpen,
  email,
  onClose,
}: SendTestEmailModalProps) => {
  const { user } = useAuth();
  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [sendTestEmail, { isLoading }] = useSendTestEmailMutation();

  useEffect(() => {
    if (isOpen) setTestEmailAddress(user?.email ?? '');
  }, [isOpen, user?.email]);

  const handleSend = async () => {
    if (!email || !testEmailAddress) return;
    try {
      const result = await sendTestEmail({
        emailId: email._id,
        testEmailAddress,
      }).unwrap();
      toast.success(result.message || `Test email sent to ${testEmailAddress}`);
      onClose();
    } catch (err) {
      const message =
        (err as { data?: { message?: string } })?.data?.message ||
        'Could not send test email';
      toast.error(message);
    }
  };

  return (
    <CommonDialog
      isOpen={isOpen}
      title="Send Test Email"
      icon="fa-light fa-paper-plane"
      confirmButtonText="Send"
      closeButtonText="Cancel"
      isLoading={isLoading}
      isConfirmDisabled={isLoading || !testEmailAddress}
      onConfirm={handleSend}
      onClose={onClose}
    >
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Sends the &ldquo;{email?.key}&rdquo; template with sample values
        substituted for any dynamic tags, so you can check formatting and
        deliverability without waiting for the real trigger event.
      </Typography>
      <TextField
        fullWidth
        type="email"
        label="Send to"
        value={testEmailAddress}
        onChange={(e) => setTestEmailAddress(e.target.value)}
        placeholder="you@example.com"
      />
      <Alert severity="info" sx={{ mt: 2 }}>
        The subject line will be prefixed with <strong>[TEST]</strong> and the
        email body will include a banner noting it's a test send.
      </Alert>
    </CommonDialog>
  );
};
